import { Box, useTheme } from "@mui/material";
import { ResizeComponent, SizeProps } from "containers/hooks/ResizeComponent";
import * as d3 from "d3";
import { Category, DataPoint } from "fileParsing/types";
import Tooltip from "containers/Components/Tooltip";
import { useState } from "react";
import CountryShapes from "../../../public/ne_110m_land.json";

export type KeyValuePair = { key: string; value: number; color?: string };

type ComponentProps = {
  data: DataPoint[];
};

type TooltipType = {
  id: number;
  x: number;
  y: number;
  data: Partial<DataPoint>;
};

function WorldMap(props: ComponentProps & SizeProps) {
  const theme = useTheme();

  const [tooltip, setToolTip] = useState<TooltipType | undefined>(undefined);

  const sphere: d3.GeoGeometryObjects = { type: "Sphere" };

  // Projection of a sphere on a flat surface
  const projection = d3.geoNaturalEarth1().fitWidth(props.size.width, sphere);
  const pathGenerator = d3.geoPath(projection);

  // Projection of DataPoints on Map
  const data = props.data
    .filter(({ longlat }) => longlat !== undefined)
    .map((point) => {
      const proj = point.longlat !== undefined ? projection(point.longlat) : null;

      return {
        x: proj?.[0],
        y: proj?.[1],
        data: point,
      };
    })
    .filter(({ x, y }) => !Number.isNaN(x) && !Number.isNaN(y));

  // scale shape by 2 from center of map
  const [[x0, y0], [x1, y1]] = pathGenerator.bounds(sphere as d3.GeoPermissibleObjects);
  const height = y1;

  let zoomScale = 1;
  let transform;
  if (data.length === 1) {
    zoomScale = 2; // don't increase, otherwise locations like Hawaii are completely isolated
    const xCenter = (x0 + x1) / 2;
    const yCenter = (y0 + y1) / 2;
    const xCenterScaled = xCenter * zoomScale;
    const yCenterScaled = yCenter * zoomScale;
    const xCenterOffset = xCenterScaled / zoomScale;
    const yCenterOffset = yCenterScaled / zoomScale;
    const xDataPointOffset = zoomScale * (data[0]?.x ?? 0);
    const yDataPointOffset = zoomScale * (data[0]?.y ?? 0);
    const xTranslate = -(xCenter + xDataPointOffset) + xCenterScaled + xCenterOffset;
    const yTranslate = -(yCenter + yDataPointOffset) + yCenterScaled + yCenterOffset;
    transform = `translate(${xTranslate}, ${yTranslate}) scale(${zoomScale}, ${zoomScale})`;
  }

  return (
    <>
      <Box
        sx={{
          width: props.size.width,
          height,
          overflow: "hidden",
        }}
      >
        <svg
          width={props.size.width}
          height={height}
          xmlns="http://www.w3.org/2000/svg"
          version="1.1"
          transform={transform}
          overflow="hidden"
        >
          <defs>
            {/* some projections bleed outside the edges of the Earth's sphere */}
            {/* let's create a clip path to keep things in bounds */}
            <clipPath id="Map__sphere">
              <path d={pathGenerator(sphere) ?? undefined} />
            </clipPath>
          </defs>
          <g style={{ clipPath: "url(#Map__sphere)" }}>
            {/* display the map itself */}
            {CountryShapes.features.map((shape) => {
              return (
                <path
                  key={shape.bbox.join("")}
                  d={pathGenerator(shape as d3.GeoPermissibleObjects) ?? undefined}
                  fill="#666"
                />
              );
            })}
          </g>
          <g style={{ clipPath: "url(#Map__sphere)" }}>
            {/* display user's location data */}
            {data.map(({ x, y, data }, ix) => {
              return (
                <circle
                  key={ix}
                  cx={x}
                  cy={y}
                  r={5 / zoomScale}
                  strokeWidth={1 / zoomScale}
                  stroke="#fff"
                  strokeOpacity={0.5}
                  fill={
                    tooltip?.id === ix ? theme.palette.action.selected : theme.palette.categories[data.category].main
                  }
                  onMouseMove={({ clientX, clientY }) => {
                    if (data.category)
                      setToolTip({
                        id: ix,
                        x: clientX,
                        y: clientY,
                        data,
                      });
                  }}
                  onMouseLeave={() => {
                    setToolTip(undefined);
                  }}
                />
              );
            })}
          </g>
        </svg>
      </Box>

      <Tooltip
        settings={{
          tooltipWidth: 400,
          margin: 0,
        }}
        visible={tooltip !== undefined}
        texts={{
          title: tooltip?.data.category,
          subtitle: tooltip?.data.subcategory,
          date: tooltip?.data.date?.toLocaleString(),
          body: tooltip?.data.text,
        }}
        uri={tooltip?.data.uri}
        category={tooltip?.data.category ?? Category.Other}
        position={{
          x: tooltip?.x ?? -1000,
          y: tooltip?.y ?? -1000,
        }}
      />
    </>
  );
}

export default ResizeComponent(WorldMap);
